import React, { useState, useEffect, lazy, Suspense, useCallback } from "react";
import { User, X, Plus, Phone, Loader2, Search, Contact as ContactIcon, Check, MapPin } from "lucide-react";
import Modal from "./Modal";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { ScrollArea } from "./ui/scroll-area";
import { PersonCategory, LocationValue, CATEGORY_OPTIONS } from "@/types";
import { useUserPreferences } from "@/contexts/UserPreferencesContext";
import { getCurrencySymbol } from "@/utils/currencyUtils";
import { authAPI } from "@/api";
import { toast } from "@/hooks/use-toast";
import { Contacts } from '@capacitor-community/contacts';
import { useDebounce } from 'use-debounce';
import { useTranslation } from 'react-i18next';
import { FormField, MonoValue } from "@/components/redesign";

// Workaround type definition for Contact
interface ContactType {
  contactId: string;
  name?: {
    display?: string;
  };
  phones?: {
    number?: string;
  }[];
}

// Async function to load phone number validation
const loadIsPossiblePhoneNumber = async (phoneNumber: string): Promise<boolean> => {
  if (!phoneNumber) return false;
  try {
    const { parsePhoneNumberFromString } = await import('libphonenumber-js/min');
    const parsed = parsePhoneNumberFromString(phoneNumber);
    return parsed?.isPossible() ?? false;
  } catch (error) {

    return false;
  }
};

// Lazy load PhoneInput
const LazyPhoneInput = lazy(async () => {
  await import('react-phone-number-input/style.css');
  const module = await import('react-phone-number-input');
  return { default: module.default };
});

// Lazy load LocationSelector
const LazyLocationSelector = lazy(() => import('./LocationSelector'));

// Helper to get label from value
const getCategoryLabel = (value: PersonCategory | undefined, t: any): string | undefined => {
    if (!value) return undefined;
    return t(`person_modal.categories.${value}`);
}

interface PersonModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: any) => void | Promise<void>;
  initialData?: {
    name: string;
    category: PersonCategory;
    specialty?: string;
    totalAgreed?: number;
    totalPaid?: number;
    phone?: string;
    location?: LocationValue;
  };
  isEditing?: boolean;
  projectId?: string;
}

const PersonModal: React.FC<PersonModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  initialData,
  isEditing = false,
  projectId
}) => {
  const { t } = useTranslation();
  const [fullName, setFullName] = useState(initialData?.name || "");
  const [category, setCategory] = useState<PersonCategory | undefined>(initialData?.category || undefined);
  const [specialty, setSpecialty] = useState(initialData?.specialty || "");
  const [totalAgreed, setTotalAgreed] = useState(initialData?.totalAgreed?.toString() || "");
  const [totalPaid, setTotalPaid] = useState(initialData?.totalPaid?.toString() || "");
  const [isAddingSpecialty, setIsAddingSpecialty] = useState(false);
  const [customSpecialty, setCustomSpecialty] = useState("");
  const { preferences } = useUserPreferences();
  const [phoneNumber, setPhoneNumber] = useState<string | undefined>(initialData?.phone || undefined);
  const [location, setLocation] = useState<LocationValue>(initialData?.location || { country: null, city: null });
  const [isFetchingUser, setIsFetchingUser] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);

  // State for contact import
  const [isContactListOpen, setIsContactListOpen] = useState(false);
  const [fetchedContacts, setFetchedContacts] = useState<ContactType[]>([]);
  const [isContactListLoading, setIsContactListLoading] = useState(false);
  const [contactSearchQuery, setContactSearchQuery] = useState("");

  // State for registration check
  const [registrationStatus, setRegistrationStatus] = useState<'idle' | 'checking' | 'registered' | 'not_registered' | 'invalid' | 'error'>('idle');
  const [registrationError, setRegistrationError] = useState<string | null>(null);

  // Debounce the phone number input for registration check
  const [debouncedPhoneNumber] = useDebounce(phoneNumber, 500);

  useEffect(() => {
    setFetchError(null);
  }, [phoneNumber]);

  const preferredCurrency = preferences?.base_currency || "TRY";
  const currencySymbol = getCurrencySymbol(preferredCurrency);

  // Updated predefinedSpecialties keys to match English PersonCategory tags
  const predefinedSpecialties: Record<string, string[]> = {
    laborer: ["Duvar Ustası", "Fayans Ustası", "Elektrik Ustası", "Sıva Ustası", "Boya Ustası", "Seramik Ustası"],
    supplier: ["İnşaat Malzemeleri", "Elektrik Malzemeleri", "Tesisat Malzemeleri", "Hırdavat"],
    subcontractor: ["Elektrik İşleri", "Tesisat İşleri", "Cam/Doğrama", "Alçı/Sıva", "Boya"],
    employer: [],
    partner: [],
    engineering: [],
    other: ["Diğer Hizmetler", "Özel Alan"],
  };

  const handleFetchUserInfo = async () => {
    setFetchError(null);
    if (!phoneNumber) {
      setFetchError(t('person_modal.errors.enter_phone_number'));
      return;
    }

    const isValid = await loadIsPossiblePhoneNumber(phoneNumber);
    if (!isValid) {
      setFetchError(t('person_modal.errors.invalid_phone_format'));
      return;
    }

    setIsFetchingUser(true);
    try {
      // Use authAPI to fetch user profile by phone
      const userData = await authAPI.getUserProfileByPhone(phoneNumber);

      if (userData && userData.selected_role) {
         // Check if the fetched role (English tag) is a valid PersonCategory
         const fetchedCategoryValue = userData.selected_role as PersonCategory;
                     const isValidCategory = CATEGORY_OPTIONS.some(opt => opt.value === fetchedCategoryValue);

        setFullName(userData.name || fullName);

        if (isValidCategory) {
            setCategory(fetchedCategoryValue); // Set state to the English tag
             const categoryLabel = getCategoryLabel(fetchedCategoryValue, t) || fetchedCategoryValue;
             toast({
               title: t('person_modal.success.user_info_fetched'),
               description: t('person_modal.success.user_info_description', { name: userData.name, category: categoryLabel })
             });
        } else {

            setCategory(undefined); // Reset category if fetched role is invalid
            setFetchError(t('person_modal.errors.invalid_user_role', { role: userData.selected_role }));
             toast({
               title: t('common.error'),
               description: t('person_modal.errors.invalid_user_role', { role: userData.selected_role }),
               variant: "destructive"
             });
        }
      } else if (userData && !userData.selected_role) {
         // User found, but no role set
         setFullName(userData.name || fullName);
         setCategory(undefined); // Reset category
         setFetchError(t('person_modal.errors.user_no_role'));
         toast({
           title: t('person_modal.success.user_info_incomplete'),
           description: t('person_modal.success.user_info_incomplete_description', { name: userData.name })
         });
      } else {
        setFetchError(t('person_modal.errors.user_not_found'));
        toast({ title: t('person_modal.success.user_not_found_title'), variant: "destructive" });
      }
    } catch (error) {

      const errorMessage = error instanceof Error ? error.message : t('person_modal.errors.user_fetch_error');
      setFetchError(errorMessage);
      toast({ title: t('common.error'), description: errorMessage, variant: "destructive" });
    } finally {
      setIsFetchingUser(false);
    }
  };

  const handleImportFromContacts = async () => {
    setIsContactListLoading(true);
    setFetchedContacts([]); // Clear previous contacts
    // Check permissions
    try {
      const permission = await Contacts.requestPermissions();
      if (permission.contacts !== 'granted') {
        toast({
          title: t('person_modal.errors.permission_denied'),
          description: t('person_modal.errors.contacts_permission_error'),
          variant: "destructive",
        });
        setIsContactListLoading(false);
        return;
      }
    } catch (error) {
       toast({
           title: t('person_modal.errors.permission_request_error'),
           description: t('person_modal.errors.contacts_permission_request_failed'),
           variant: "destructive",
       });
       setIsContactListLoading(false);
       return;
    }

    // Get contacts
    try {
      const result = await Contacts.getContacts({
          projection: {
              name: true,
              phones: true,
          }
      });

      if (!result || !result.contacts || result.contacts.length === 0) {
        toast({
          title: t('person_modal.errors.no_contacts_found'),
          description: t('person_modal.errors.no_contacts_to_import'),
        });
        setIsContactListLoading(false);
        return;
      }

      // Store fetched contacts and open the selection modal
      setFetchedContacts(result.contacts);
      setIsContactListOpen(true);

    } catch (error) {

      toast({
        title: t('person_modal.errors.contacts_import_error'),
        description: t('person_modal.errors.contacts_fetch_failed'),
        variant: "destructive",
      });
    } finally {
        setIsContactListLoading(false);
    }
  };

  // Helper function to format phone number for DB
  const formatPhoneNumberForDB = useCallback((rawPhone: string | undefined): string => {
    if (!rawPhone) return "";

    // 1. Remove non-digit characters
    let digitsOnly = rawPhone.replace(/\D/g, '');

    // 2. Remove country code (assuming TR -> 90)
    //    TODO: Make this dynamic based on defaultCountry if needed
    if (digitsOnly.startsWith('90')) {
        digitsOnly = digitsOnly.substring(2);
    }

    // 3. Remove leading zeros
    return digitsOnly.replace(/^0+/, '');
  }, []);

  // Effect to check registration status based on debounced phone number
  useEffect(() => {
    const checkRegistration = async () => {
      const formattedNumber = formatPhoneNumberForDB(debouncedPhoneNumber);

      // Adjust length check based on your target countries/formats if needed (e.g., 10 for TR)
      if (!formattedNumber || formattedNumber.length < 10) {
        setRegistrationStatus('idle'); // Reset if number is too short or empty/invalid format
        setRegistrationError(null);
        return;
      }

      setRegistrationStatus('checking');
      setRegistrationError(null);

      try {
        const userData = await authAPI.getUserProfileByPhone(formattedNumber); // Use formatted number

        if (userData) {
          setRegistrationStatus('registered');
        } else {
          // Assuming API returns null or specific error for not found
          setRegistrationStatus('not_registered');
        }
      } catch (error: any) {
         // More specific error handling if needed based on API response
        if (error.message && error.message.toLowerCase().includes('not found')) { // Example check
             setRegistrationStatus('not_registered');
        } else {

            setRegistrationStatus('error');
            setRegistrationError(t('person_modal.registration_status.registration_check_error')); // User-friendly message
        }
      }
    };

    // Trigger check only if debouncedPhoneNumber has a value
    if (debouncedPhoneNumber) {
        checkRegistration();
    } else {
        setRegistrationStatus('idle'); // Reset if phone number is cleared
        setRegistrationError(null);
    }
    // Ensure initial check runs if modal opens with a phone number
    // This depends on when initialData.phone sets the phoneNumber state initially
  }, [debouncedPhoneNumber, formatPhoneNumberForDB, t]);

  // Add initial check if modal opens with data
  useEffect(() => {
      if (isOpen && initialData?.phone) {
          const formattedNumber = formatPhoneNumberForDB(initialData.phone);
           if (formattedNumber && formattedNumber.length >= 10) {
               // Trigger the check manually or rely on the debounced effect?
               // Relying on debounce might be simpler if state update triggers it
           }
      }
  }, [isOpen, initialData, formatPhoneNumberForDB]);

  // Handle selecting a contact from the modal
  const handleContactSelect = (contact: ContactType) => {
      const contactName = contact.name?.display || "";
      // Find the first available phone number
      const rawPhone = contact.phones && contact.phones.length > 0
                       ? contact.phones[0].number
                       : "";

      const formattedPhone = formatPhoneNumberForDB(rawPhone);

      if (!formattedPhone) {
           toast({
              title: t('person_modal.no_phone_number'),
              description: t('person_modal.errors.selected_contact_no_phone', { name: contactName || t('common.unknown') }),
              variant: "default",
          });
          // Don't close the modal if no phone number
          return;
      }

      // Update state
      if (contactName) {
          setFullName(contactName);
      }
      setPhoneNumber(formattedPhone); // Set the cleaned phone number
      setIsContactListOpen(false); // Close the selection modal

      toast({
        title: t('person_modal.contact_imported'),
        description: `${contactName ? contactName + " " + t('person_modal.contact_phone_added') : t('person_modal.contact_phone_added')}`,
      });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!category) {
        toast({
          title: t('person_modal.errors.missing_category'),
          description: t('person_modal.errors.select_category'),
          variant: "destructive"
        });
        return;
    }
    const finalPhoneNumber = formatPhoneNumberForDB(phoneNumber); // Format before submitting
    try {
      await Promise.resolve(
        onSubmit({
          name: fullName,
          category: category,
          specialty: isAddingSpecialty ? customSpecialty : specialty,
          totalAgreed: totalAgreed ? parseFloat(totalAgreed) : 0,
          totalPaid: totalPaid ? parseFloat(totalPaid) : 0,
          phone: finalPhoneNumber,
          location: location,
          country: location.country?.label || null,
          city: location.city?.label || null
        })
      );
      resetForm();
      onClose();
    } catch {
      // Parent handles submission errors and toasts.
    }
  };

  const resetForm = () => {
    if (!initialData) {
      setFullName("");
      setCategory(undefined);
      setSpecialty("");
      setTotalAgreed("");
      setTotalPaid("");
      setIsAddingSpecialty(false);
      setCustomSpecialty("");
      setPhoneNumber(undefined);
      setLocation({ country: null, city: null });
      setIsFetchingUser(false);
      setFetchError(null);
    } else {
       // Handle resetting edit form if needed
       setFullName(initialData.name);
       setCategory(initialData.category);
       setSpecialty(initialData.specialty || "");
       setTotalAgreed(initialData.totalAgreed?.toString() || "");
       setTotalPaid(initialData.totalPaid?.toString() || "");
       setPhoneNumber(initialData.phone || undefined);
       setLocation(initialData.location || { country: null, city: null });
       setIsAddingSpecialty(false);
       setCustomSpecialty("");
       setIsFetchingUser(false);
       setFetchError(null);
    }
    // Also reset contact search state when form resets
    setIsContactListOpen(false);
    setFetchedContacts([]);
    setContactSearchQuery("");
    setIsContactListLoading(false);
    // Explicitly reset registration status here too for immediate UI feedback
    setRegistrationStatus('idle');
    setRegistrationError(null);
  };

  // Reset form fields when modal is closed or opened (if not editing)
  useEffect(() => {
      if (isOpen) {
          resetForm(); // Reset when opened (respects initialData)
      }
  }, [isOpen, initialData]); // Rerun when isOpen or initialData changes

  const handleAddSpecialty = () => {
    if (!customSpecialty.trim() || !category) return;

    if (!predefinedSpecialties[category]) {
        predefinedSpecialties[category] = [];
    }

    if (!predefinedSpecialties[category].includes(customSpecialty)) {
      predefinedSpecialties[category] = [...predefinedSpecialties[category], customSpecialty];
    }

    setSpecialty(customSpecialty);
    setIsAddingSpecialty(false);
    setCustomSpecialty("");
  };

  // Filter specialty options based on the selected category (English tag)
  const specialtyOptions = category ? (predefinedSpecialties[category] || []) : [];
  // Show specialty section only for specific categories (using English tags)
  const showSpecialtySection = category && ["laborer", "supplier", "subcontractor"].includes(category);

  // Filter contacts based on search query
  const filteredContacts = fetchedContacts.filter(contact => {
    const displayName = contact.name?.display?.toLowerCase() || "";
    return displayName.includes(contactSearchQuery.toLowerCase());
  });

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEditing ? t('person_modal.title_edit') : t('person_modal.title_add')}
      className="max-w-md"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Phone Number Input */}
        <FormField
          label={t('person_modal.phone_number_label')}
          htmlFor="phoneNumber"
        >
          <div className="flex gap-2 items-start">
            <Suspense fallback={
              <div className="flex-grow h-10 flex items-center justify-center bg-gray-100 rounded-md">
                <Loader2 className="h-5 w-5 animate-spin text-brand-600" />
              </div>
            }>
              <div className="relative flex-grow phone-input-container">
                <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 z-10" size={18} />
                <LazyPhoneInput
                  id="phoneNumber"
                  placeholder={t('person_modal.phone_placeholder')}
                  value={phoneNumber}
                  onChange={setPhoneNumber}
                  className="pl-10 pr-12"
                  defaultCountry="TR"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-1 top-1/2 transform -translate-y-1/2 h-8 w-8 text-gray-400 hover:text-brand-600"
                  onClick={handleFetchUserInfo}
                  disabled={isFetchingUser || !phoneNumber}
                  aria-label={t('person_modal.find_user_aria')}
                >
                  {isFetchingUser ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search size={16} />}
                </Button>
              </div>
            </Suspense>
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="h-10 w-10 flex-shrink-0 border-gray-300 text-gray-600 hover:text-brand-600 hover:border-brand-300"
              onClick={handleImportFromContacts}
              aria-label={t('person_modal.import_contacts_aria')}
              disabled={isContactListLoading}
            >
              {isContactListLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ContactIcon size={16} />}
            </Button>
          </div>

          {/* Display Fetch User Error */}
          {fetchError && (
            <p className="text-[11px] text-error-600 mt-1">{fetchError}</p>
          )}

          {/* Registration Status Display */}
          <div className="mt-1.5 text-[12px] h-5 flex items-center gap-1.5">
            {registrationStatus === 'checking' && (
              <>
                <Loader2 className="h-3.5 w-3.5 animate-spin text-gray-400" />
                <span className="text-gray-500">{t('person_modal.registration_status.checking')}</span>
              </>
            )}
            {registrationStatus === 'registered' && (
              <>
                <Check className="h-3.5 w-3.5 text-success-600" />
                <span className="text-success-600 font-medium">{t('person_modal.registration_status.registered')}</span>
              </>
            )}
            {registrationStatus === 'not_registered' && (
              <>
                <X className="h-3.5 w-3.5 text-error-600" />
                <span className="text-error-600 font-medium">{t('person_modal.registration_status.not_registered')}</span>
              </>
            )}
            {registrationStatus === 'error' && (
              <span className="text-error-600 text-[11px]">{registrationError || t('person_modal.registration_status.status_check_failed')}</span>
            )}
          </div>
        </FormField>

        {/* Full Name */}
        <FormField
          label={t('person_modal.full_name_label')}
          required
          htmlFor="fullName"
        >
          <div className="relative">
            <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              id="fullName"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              className="pl-9 border-gray-300 focus:border-brand-600 rounded-lg"
              placeholder={t('person_modal.full_name_placeholder')}
              required
              readOnly={isFetchingUser}
            />
          </div>
        </FormField>

        {/* Category */}
        <FormField
          label={t('person_modal.category_label')}
          required
          htmlFor="category"
        >
          <Select
            value={category}
            onValueChange={(value: PersonCategory) => {
              setCategory(value);
              setSpecialty('');
            }}
            disabled={isFetchingUser}
          >
            <SelectTrigger className="border-gray-300 focus:border-brand-600 rounded-lg">
              <SelectValue placeholder={t('person_modal.category_placeholder')} />
            </SelectTrigger>
            <SelectContent>
              {CATEGORY_OPTIONS.map(option => (
                <SelectItem key={option.value} value={option.value}>
                  {t(`person_modal.categories.${option.value}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FormField>

        {/* Specialty (Conditionally Rendered) */}
        {showSpecialtySection && (
          <FormField
            label={t('person_modal.specialty_label')}
            htmlFor="specialty"
          >
            {isAddingSpecialty ? (
              <div className="space-y-2">
                <div className="flex gap-2">
                  <Input
                    value={customSpecialty}
                    onChange={(e) => setCustomSpecialty(e.target.value)}
                    placeholder={t('person_modal.new_specialty_placeholder')}
                    className="flex-grow border-gray-300 focus:border-brand-600 rounded-lg"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={handleAddSpecialty}
                    disabled={!customSpecialty || !category}
                    className="border-gray-300 text-gray-600 hover:border-brand-300 hover:text-brand-600"
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() => setIsAddingSpecialty(false)}
                    className="border-gray-300 text-gray-600 hover:border-gray-400"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <Select
                  value={specialty}
                  onValueChange={setSpecialty}
                  disabled={!category || specialtyOptions.length === 0 || isFetchingUser}
                >
                  <SelectTrigger className="border-gray-300 focus:border-brand-600 rounded-lg">
                    <SelectValue placeholder={specialtyOptions.length > 0 ? t('person_modal.specialty_placeholder') : t('person_modal.specialty_no_options')} />
                  </SelectTrigger>
                  <SelectContent>
                    {specialtyOptions.map(option => (
                      <SelectItem key={option} value={option}>{option}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setIsAddingSpecialty(true)}
                  className="w-full border-gray-300 text-gray-600 hover:border-brand-300 hover:text-brand-600 rounded-lg"
                  disabled={!category || isFetchingUser}
                >
                  <Plus className="h-4 w-4 mr-2" /> {t('person_modal.add_new_specialty')}
                </Button>
              </div>
            )}
          </FormField>
        )}

        {/* Location Selector */}
        <FormField
          label={
            <span className="flex items-center gap-1.5">
              <MapPin className="h-3.5 w-3.5" /> {t('person_modal.location_label')}
            </span>
          }
        >
          <Suspense fallback={
            <div className="h-20 flex items-center justify-center bg-gray-100 rounded-lg text-sm text-gray-500">
              {t('person_modal.location_loading')}
            </div>
          }>
            <LazyLocationSelector
              value={location}
              onChange={setLocation}
            />
          </Suspense>
        </FormField>

        {/* Financials (Conditionally Rendered) */}
        {projectId && (
          <>
            <FormField
              label={`${t('person_modal.total_agreed_label')} (${currencySymbol})`}
              htmlFor="totalAgreed"
            >
              <Input
                id="totalAgreed"
                type="number"
                value={totalAgreed}
                onChange={(e) => setTotalAgreed(e.target.value)}
                placeholder={t('person_modal.total_agreed_placeholder', { currency: currencySymbol })}
                disabled={isFetchingUser}
                className="border-gray-300 focus:border-brand-600 rounded-lg font-mono"
              />
              {totalAgreed && (
                <div className="mt-1 flex items-center gap-1.5">
                  <span className="text-[11px] text-gray-500">{t('person_modal.total_agreed_label')}:</span>
                  <MonoValue value={parseFloat(totalAgreed) || 0} format suffix="₺" size="sm" tone="default" />
                </div>
              )}
            </FormField>

            <FormField
              label={`${t('person_modal.total_paid_label')} (${currencySymbol})`}
              htmlFor="totalPaid"
            >
              <Input
                id="totalPaid"
                type="number"
                value={totalPaid}
                onChange={(e) => setTotalPaid(e.target.value)}
                placeholder={t('person_modal.total_paid_placeholder', { currency: currencySymbol })}
                disabled={isFetchingUser}
                className="border-gray-300 focus:border-brand-600 rounded-lg font-mono"
              />
              {totalPaid && (
                <div className="mt-1 flex items-center gap-1.5">
                  <span className="text-[11px] text-gray-500">{t('person_modal.total_paid_label')}:</span>
                  <MonoValue value={parseFloat(totalPaid) || 0} format suffix="₺" size="sm" tone="success" />
                </div>
              )}
            </FormField>
          </>
        )}

        {/* Submit Button */}
        <Button
          type="submit"
          className="w-full bg-brand-600 hover:bg-brand-700 text-white rounded-xl h-11 font-semibold"
          disabled={isFetchingUser || !category}
        >
          {isEditing ? t('person_modal.update_person_button') : t('person_modal.add_person_button')}
        </Button>
      </form>

      {/* Contact Selection Modal */}
      <Modal
        isOpen={isContactListOpen}
        onClose={() => {
             setIsContactListOpen(false);
             setContactSearchQuery(""); // Reset search on close
        }}
        title={t('person_modal.select_contact_title')}
        className="max-w-md"
      >
        {/* Search Input */}
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 shadow-sm">
            <Search className="h-4 w-4 text-gray-400 flex-shrink-0" />
            <input
              className="w-full bg-transparent text-[14px] text-gray-700 outline-none placeholder:text-gray-400"
              placeholder={t('person_modal.search_contacts_placeholder')}
              value={contactSearchQuery}
              onChange={(e) => setContactSearchQuery(e.target.value)}
            />
          </div>
        </div>

        {/* Contact List */}
        <ScrollArea className="h-[50vh] max-h-[350px] p-4">
          {filteredContacts.length > 0 ? (
            <div className="space-y-2">
              {filteredContacts.map((contact) => {
                const displayName = contact.name?.display || t('common.unknown');
                const firstPhone = contact.phones && contact.phones.length > 0 ? contact.phones[0].number : t('person_modal.no_phone_number');
                return (
                  <button
                    key={contact.contactId}
                    type="button"
                    className="w-full text-left px-3 py-2.5 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 hover:border-brand-300 transition-colors"
                    onClick={() => handleContactSelect(contact)}
                  >
                    <p className="font-medium text-gray-900 text-[14px]">{displayName}</p>
                    <MonoValue value={firstPhone ?? ''} size="sm" tone="muted" className="mt-0.5" />
                  </button>
                );
              })}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-4 text-sm">
              {contactSearchQuery ? t('person_modal.no_matching_contacts') : (fetchedContacts.length > 0 ? t('person_modal.all_contacts_shown') : t('person_modal.contact_list_empty'))}
            </p>
          )}
        </ScrollArea>
      </Modal>
    </Modal>
  );
};

export default PersonModal;
